import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, PageAlert, Txt } from '@vst/beam';
import { useTranslation } from 'react-i18next';

import {
  AnalyticsEventNames,
  Categories,
  DataLayerEventName,
  pushToAdobeDataLayer,
} from '@app/services/adobeDataLayer';
import { PaymentMethodLogo } from '@app/components/PaymentMethodLogo';
import { AlertType, DisplayLocation, removeAlert, setBillingAlerts } from '@app/redux/alerts';
import { refetchBillingInfo, selectBillingInfo } from '@app/redux/billingInfo';
import { Route, setBillingNav } from '@app/redux/billingNav';
import { selectConfig } from '@app/redux/config';
import { selectLocale } from '@app/redux/locale';
import {
  PaymentMethodType,
  resetPayments,
  retrieveTransactionId,
  selectPayments,
  setPayments,
} from '@app/redux/payments';
import { scrollToTop } from '@app/redux/utils';

import { Locale, NotificationLevel, VppError } from '@mfe/shared/schema-types';

import Card from '../components/card';
import Divider from '../components/divider';
import Gradient from '../components/gradient';
import { TitleText } from '../components/title';
import DifferentMethod from './different-method';
import {
  getAutopayPageAlertTitle,
  getPageAlertDescription,
  handleAutopayMessages,
  handleCommonVppMessages,
  iframeProps,
  parseVppEvent,
  postMessageToVPS,
  VppEventCode,
} from './utils';

import styles from './vpp.module.scss';

const INIT_AUTOPAY = JSON.stringify({
  initConnection: true,
  pageFunction: 'Wallet',
});

const AutoPay = () => {
  const {
    billingInfo: { autoPay },
  } = useSelector(selectBillingInfo);
  const { vppUrl, isPreauthZeroEnabled } = useSelector(selectConfig);
  const {
    locale: { userLocale: locale },
  } = useSelector(selectLocale);
  const payments = useSelector(selectPayments);
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { url, height, paymentMethodType, error, loading, frameReady } = payments;

  let preAuthMessage = '';
  if (!isPreauthZeroEnabled) {
    preAuthMessage = t('VPP:bankAndCCPreAuthDisclaimer');
  }

  const showCurrentMethod = autoPay && locale === Locale.EnUs;

  const paymentDetails = React.useMemo(() => ({ payment_method_type: paymentMethodType }), [paymentMethodType]);

  React.useLayoutEffect(() => {
    dispatch(resetPayments());
  }, [dispatch]);

  const onCancel = React.useCallback(() => dispatch(setBillingNav({ route: Route.Home })), [dispatch]);

  const onSuccess = React.useCallback(() => {
    pushToAdobeDataLayer({
      event: DataLayerEventName.FORM_SUBMIT,
      event_name: DataLayerEventName.FORM_SUBMIT,
      event_label: `${AnalyticsEventNames.AutopayCompleted} | PaymentMethodType: ${paymentDetails.payment_method_type}`,
      event_category: Categories.Billing,
    });

    dispatch(refetchBillingInfo());
    dispatch(removeAlert(AlertType.NoValidPaymentMethod));
    dispatch(removeAlert(AlertType.PaymentMethodExpired));
    dispatch(
      setBillingAlerts([
        {
          level: NotificationLevel.Success,
          title: t('Alerts:Billing.PaymentMethodUpdated.title'),
          caption: t('Alerts:Billing.PaymentMethodUpdated.caption'),
          type: AlertType.PaymentMethodUpdated,
          displayLocation: DisplayLocation.LOCAL,
        },
      ])
    );
    dispatch(setBillingNav({ route: Route.Home }));
    dispatch(scrollToTop());
  }, [dispatch, paymentDetails, t]);

  const onError = React.useCallback(
    (error?: VppError) => {
      const paymentDetailsFailure = {
        ...paymentDetails,
        failure_reason: error?.message ?? 'Update Failed.',
      };

      pushToAdobeDataLayer({
        event: DataLayerEventName.FORM_SUBMIT,
        event_name: DataLayerEventName.FORM_SUBMIT,
        event_label: `${AnalyticsEventNames.AutopayFailed} | PaymentMethodType: ${paymentDetailsFailure.payment_method_type}`,
        event_category: Categories.Billing,
      });

      dispatch(setPayments({ error, submit: false }));
      dispatch(scrollToTop());
    },
    [dispatch, paymentDetails]
  );

  React.useEffect(() => {
    const handleIframeMessage = (event: MessageEvent) => {
      const eventData = parseVppEvent(event, vppUrl);

      const handleVppMessage = [VppEventCode.PAYMENT_TYPE_CHANGED, VppEventCode.UPDATE_PMT_MTHD_CLICKED].includes(
        eventData?.eventCode as VppEventCode
      );

      const context = { ...payments, onSuccess, onError, onCancel };

      if (handleVppMessage) {
        handleAutopayMessages(event, context, vppUrl, dispatch, isPreauthZeroEnabled);
      } else {
        handleCommonVppMessages(event, context, vppUrl, dispatch);
      }
    };
    window.addEventListener('message', handleIframeMessage);

    return () => window.removeEventListener('message', handleIframeMessage);
  }, [payments, vppUrl, onSuccess, onError, onCancel, dispatch]);

  React.useEffect(() => {
    dispatch(retrieveTransactionId(undefined));
  }, [dispatch]);

  return (
    <div className={styles['wrapper']}>
      <div className={styles['paymentContainer']}>
        {error && (
          <div className={styles['notificationsContainer']}>
            <PageAlert
              title={getAutopayPageAlertTitle(error, t)}
              variant={'error'}
              description={getPageAlertDescription(error, t)}
              closeable={true}
              onClose={() => {
                dispatch(setPayments({ error: null }));
                dispatch(retrieveTransactionId(undefined));
              }}
            />
          </div>
        )}

        <Card className={styles['card']}>
          <Gradient />

          <div className={styles['container']}>
            <div className={styles['titleContainer']} data-cy="make-payment-page-title">
              <Txt variant="heading4">{t('VPP:updatePaymentMethod')}</Txt>
            </div>
            {showCurrentMethod && (
              <>
                <div className={styles['statusContainer']}>
                  <TitleText title={t('VPP:currentPaymentMethodTitle')} style={{ paddingBottom: 12 }}>
                    <div className={styles['label']}>
                      <PaymentMethodLogo name={autoPay?.paymentMethod} width="44px" />
                      <Txt variant="bodySmallRegular" color="regular">
                        {t('VPP:paymentMethod', {
                          ...autoPay,
                          count: autoPay?.last4Digits?.length,
                        })}
                      </Txt>
                    </div>
                  </TitleText>
                </div>
                <div className={styles['statusContainer']}>
                  <div className={styles['terms']}>
                    <Txt variant="bodySmallRegular" color="regular">
                      {t('VPP:currentPaymentMethodTerms')}
                    </Txt>
                  </div>
                </div>
              </>
            )}
            <div className={styles['differentMethodContainer']}>
              {loading && (
                <DifferentMethod frameReady={frameReady}>
                  <iframe
                    title="Update Payment Method"
                    height={height}
                    src={url}
                    onLoad={() => postMessageToVPS(INIT_AUTOPAY, url)}
                    {...iframeProps}
                  />
                </DifferentMethod>
              )}
            </div>

            {(paymentMethodType === PaymentMethodType.CARD || paymentMethodType === PaymentMethodType.BANK) && (
              <Txt variant="smallRegular" component="p" style={{ padding: '0px 48px 24px' }}>
                {preAuthMessage}
              </Txt>
            )}
            {!frameReady && (
              <>
                <Divider />
                <div className={styles['footerContainer']}>
                  <AutoPayButtons onCancel={onCancel} />
                </div>
              </>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

const AutoPayButtons = ({ onCancel }: { onCancel: () => void }) => {
  const { t } = useTranslation('VPP');

  return (
    <div className={styles['paymentButtonsContainer']}>
      <Button variant="secondary" className={styles['cancelButton']} onClick={onCancel}>
        <Txt variant="labelMedium">{t('cancelButton')}</Txt>
      </Button>

      <Button className={styles['paymentButton']} disabled={true}>
        <Txt variant="labelMedium">{t('updatePaymentMethod')}</Txt>
      </Button>
    </div>
  );
};

export default AutoPay;
